<template>
  <div>
    <b-container fluid>
      <b-row>
        <b-col cols="12">
          <h2>Cupons de Desconto</h2>

          <!-- Statistics Cards -->
          <b-row class="mb-4">
            <b-col cols="12" md="6" class="mb-3">
              <b-card no-body class="h-100">
                <b-card-body>
                  <h6 class="text-muted mb-2">
                    Impacto Financeiro dos Descontos
                    <span v-b-tooltip.hover title="Valor total concedido em descontos.">
                      <icon name="info-circle" scale="0.8"></icon>
                    </span>
                  </h6>
                  <h3 class="text-primary mb-0" v-if="!loading">
                    {{ stats.total_discount | currency }} em descontos aplicados
                  </h3>
                  <b-spinner v-else small></b-spinner>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col cols="12" md="6" class="mb-3">
              <b-card no-body class="h-100">
                <b-card-body>
                  <h6 class="text-muted mb-2">
                    Vendas com Cupom vs Sem Cupom
                    <span v-b-tooltip.hover title="Comparação entre pedidos que usaram cupom e pedidos normais.">
                      <icon name="info-circle" scale="0.8"></icon>
                    </span>
                  </h6>
                  <h3 class="text-success mb-0" v-if="!loading">
                    {{ stats.coupon_percentage.toFixed(0) }}% das vendas usaram cupom
                  </h3>
                  <b-spinner v-else small></b-spinner>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <!-- Filters and Actions -->
          <b-row class="mb-3">
            <b-col cols="12" lg="6">
              <p class="mb-2 font-weight-bold d-none">Filtrar por status</p>
              <b-form-group class="d-none">
                <b-form-radio v-model="activeFilter" value="all" @change="loadCoupons">Todos</b-form-radio>
                <b-form-radio v-model="activeFilter" value="active" @change="loadCoupons">Ativos</b-form-radio>
                <b-form-radio v-model="activeFilter" value="inactive" @change="loadCoupons">Inativos</b-form-radio>
              </b-form-group>
            </b-col>

            <b-col cols="12" lg="6" class="d-flex align-items-end justify-content-lg-end mb-3">
              <b-button variant="primary" @click="openCreateModal">
                <icon scale="0.9" class="mr-1" name="plus"></icon> Novo Cupom
              </b-button>
            </b-col>
          </b-row>

          <!-- Coupons Table -->
          <b-row>
            <b-col>
              <b-table
                :items="coupons"
                :fields="tableFields"
                :busy="loading"
                striped
                hover
                responsive
                show-empty
                empty-text="Nenhum cupom encontrado"
                class="my-4"
              >
            <template #cell(code)="data">
              <div class="d-flex align-items-center">
                <b-button
                  size="sm"
                  variant="primary"
                  @click="copyCouponCode(data.value)"
                  v-b-tooltip.hover
                  title="Copiar código"
                  class="pl-2 pr-0"
                >
                  <icon name="copy" scale="0.8"></icon>
                </b-button>
                <strong class="ml-3">{{ data.value }}</strong>
              </div>
            </template>

            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.item)" class="px-2 py-1" style="font-size: 0.75rem;">
                {{ getStatusLabel(data.item) }}
              </b-badge>
            </template>

            <template v-slot:cell(stock_fmted)="data">
              <span v-if="data.item.stock === null" class="text-primary">Ilimitado</span>
              <span v-else-if="data.item.stock === 0" class="text-danger">Sem estoque</span>
              <span v-else-if="data.item.stock === 1">{{ data.item.stock }} unidade</span>
              <span v-else>{{ data.item.stock | number }} unidades</span>
            </template>            

            <template #cell(period)="data">
              <div style="font-size: 0.85rem; line-height: 1.4;">
                <div><strong>De:</strong> {{ $options.filters.date(data.item.starts_at) }}</div>
                <div><strong>Até:</strong> {{ $options.filters.date(data.item.expires_at) }}</div>
              </div>
            </template>

            <template #cell(discount)="data">
              <span v-if="data.item.discount_type === 'percentage'">
                {{ (data.item.discount_value / 100).toFixed(2) }}%
              </span>
              <span v-else>
                {{ data.item.discount_value | currency }}
              </span>
            </template>

            <template #cell(min_order_value)="data">
              <span v-if="data.value">
                {{ $options.filters.currency(data.value) }}
              </span>
              <span v-else class="text-muted">
                -
              </span>
            </template>

            <template #cell(max_discount_value)="data">
              <span v-if="data.value">
                {{ $options.filters.currency(data.value) }}
              </span>
              <span v-else class="text-muted">
                -
              </span>
            </template>

            <template #cell(stock)="data">
              <span v-if="data.value === null" class="text-info font-weight-bold">
                Ilimitado
              </span>
              <span v-else :class="{'text-danger font-weight-bold': data.value === 0}">
                {{ data.value }}
              </span>
            </template>

            <template #cell(reserved_stock)="data">
              <span :class="{'text-warning font-weight-bold': data.value > 0}">
                {{ data.value }}
              </span>
            </template>

            <template #cell(active)="data">
              <toggle-button
                :value="data.item.active"
                @change="toggleActive(data.item)"
                :sync="true"
              />
            </template>

            <template #cell(actions)="data">
              <div>
                <b-dropdown variant="secondary" size="sm" class="mr-1" no-caret boundary="window">
                  <template #button-content>
                    <icon name="ellipsis-v" scale="1" class="mt-2 mb-0 ml-2"></icon>
                  </template>

                  <b-dropdown-item @click="openEditModal(data.item)">
                    <icon name="edit" scale="1" class="mr-2"></icon>
                    Editar
                  </b-dropdown-item>

                  <b-dropdown-divider></b-dropdown-divider>

                  <span
                    v-b-tooltip.hover
                    :title="data.item.usage_count > 0 ?
                      'Cupom já foi utilizado em pedidos e não pode ser excluído' :
                      'Excluir cupom'">
                    <b-dropdown-item
                      @click="deleteCoupon(data.item)"
                      variant="danger"
                      :disabled="data.item.usage_count > 0">
                      <icon name="trash" scale="1" class="mr-2"></icon>
                      Excluir
                    </b-dropdown-item>
                  </span>
                </b-dropdown>
              </div>
            </template>
              </b-table>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>

    <!-- Create/Edit Modal -->
    <coupon-form
      :visible="showModal"
      :coupon="selectedCoupon"
      :organizer="organizer"
      @close="closeModal"
      @saved="onCouponSaved"
    />
  </div>
</template>

<script>
import CouponForm from '@/components/CouponForm.vue'
import { ToggleButton } from 'vue-js-toggle-button'

export default {
  name: 'Coupons',
  components: {
    CouponForm,
    ToggleButton
  },
  data() {
    return {
      organizer: null,
      coupons: [],
      loading: false,
      stats: {
        total_orders: 0,
        orders_with_coupon: 0,
        total_discount: 0,
        coupon_percentage: 0
      },
      activeFilter: 'all',
      showModal: false,
      selectedCoupon: null,
      tableFields: [
        { key: 'code', label: 'Código', sortable: true, class: ['text-nowrap', 'align-middle'] },
        { key: 'status', label: 'Situação', sortable: true, sortByFormatted: true, formatter: (value, key, item) => this.getStatusLabel(item) },
        { key: 'discount', label: 'Desconto', class: ['text-center', 'text-nowrap', 'align-middle'] },
        { key: 'min_order_value', label: 'Pedido Mín.', class: ['text-center', 'text-nowrap', 'align-middle'] },
        { key: 'max_discount_value', label: 'Desc. Máx.', class: ['text-center', 'text-nowrap', 'align-middle'] },
        { key: 'stock_fmted', label: 'Estoque', sortable: true, class: ['text-center', 'text-nowrap', 'align-middle'] },
        { key: 'reserved_stock', label: 'Reservado', sortable: true, class: ['text-center', 'text-nowrap', 'align-middle'] },
        { key: 'period', label: 'Período', class: ['text-center', 'text-nowrap', 'align-middle'] },
        { key: 'active', label: 'Ativo' },
        { key: 'actions', label: 'Ações' }
      ]
    }
  },
  mounted() {
    this.organizer = this.$route.params.organizer
    this.loadCoupons()
  },
  methods: {
    async loadCoupons() {
      this.loading = true
      const activeParam = this.activeFilter === 'all' ? '' : `?active=${this.activeFilter === 'active'}`

      try {
        const response = await this.$http.get(`${process.env.VUE_APP_URL_APP}/b/organizers/${this.organizer}/coupons${activeParam}`)
        this.coupons = response.body.coupons
        this.stats = response.body.stats
      } catch (error) {
        this.$bvToast.toast('Erro ao carregar cupons', { variant: 'danger' })
      } finally {
        this.loading = false
      }
    },

    openCreateModal() {
      this.selectedCoupon = null
      this.showModal = true
    },

    openEditModal(coupon) {
      this.selectedCoupon = coupon
      this.showModal = true
    },

    closeModal() {
      this.showModal = false
      this.selectedCoupon = null
    },

    onCouponSaved() {
      this.closeModal()
      this.loadCoupons()
    },

    async toggleActive(coupon) {
      try {
        await this.$http.put(`${process.env.VUE_APP_URL_APP}/b/organizers/${this.organizer}/coupons/${coupon.id}`, {
          active: !coupon.active
        })
        this.loadCoupons()
      } catch (error) {
        this.$bvToast.toast('Erro ao atualizar cupom', { variant: 'danger' })
      }
    },

    async deleteCoupon(coupon) {
      const action = await this.$bvModal.msgBoxConfirm(`Deseja realmente excluir o cupom ${coupon.code}?`, {
        title: '⚠ Atenção',
        okVariant: 'danger',
        cancelVariant: 'secondary',
        okTitle: 'Excluir',
        cancelTitle: 'Cancelar',
        centered: true,
        hideHeaderClose: false
      })

      if (action === false || !action) {
        return false
      }

      try {
        await this.$http.delete(`${process.env.VUE_APP_URL_APP}/b/organizers/${this.organizer}/coupons/${coupon.id}`)
        this.$bvToast.toast('Cupom excluído com sucesso', { variant: 'success' })
        this.loadCoupons()
      } catch (error) {
        this.$bvToast.toast('Erro ao excluir cupom', { variant: 'danger' })
      }
    },

    copyCouponCode(code) {
      // Create a temporary textarea to copy the code
      const textarea = document.createElement('textarea')
      textarea.value = code
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()

      try {
        document.execCommand('copy')
        this.$bvToast.toast(`Código "${code}" copiado!`, {
          variant: 'success',
          autoHideDelay: 2000,
          solid: true
        })
      } catch (err) {
        this.$bvToast.toast('Erro ao copiar código', {
          variant: 'danger'
        })
      } finally {
        document.body.removeChild(textarea)
      }
    },

    getStatusLabel(coupon) {
      // Precedence: Esgotado > Expirado > Agendado > Disponível

      // 1. Check if stock exhausted (highest priority)
      if (coupon.stock !== null && coupon.stock <= 0) {
        return 'Esgotado'
      }

      const now = new Date()
      const expiresAt = new Date(coupon.expires_at)
      const startsAt = new Date(coupon.starts_at)

      // 2. Check if expired
      if (now > expiresAt) {
        return 'Expirado'
      }

      // 3. Check if not started yet
      if (now < startsAt) {
        return 'Agendado'
      }

      // 4. Active and available
      return 'Disponível'
    },

    getStatusVariant(coupon) {
      // Precedence: Esgotado > Expirado > Agendado > Disponível

      // 1. Check if stock exhausted (RED - most critical)
      if (coupon.stock !== null && coupon.stock <= 0) {
        return 'danger'
      }

      const now = new Date()
      const expiresAt = new Date(coupon.expires_at)
      const startsAt = new Date(coupon.starts_at)

      // 2. Check if expired (ORANGE - critical)
      if (now > expiresAt) {
        return 'warning'
      }

      // 3. Check if not started yet (BLUE - informational)
      if (now < startsAt) {
        return 'info'
      }

      // 4. Active and available (GREEN - success)
      return 'success'
    }
  }
}
</script>
